// Iter-194n — Admin documents migration wizard (CEO only).
//
// Walks the CEO through every client whose administrative documents still
// live outside the dedicated `005-Documents Administratifs` Drive subfolder.
// For each client, one click:
//   • Creates the subfolder if missing
//   • Moves every Drive-backed doc into it (`files.update` addParents/removeParents)
//   • Re-uploads any local-fallback doc to Drive then unlinks the local file
//   • Records `drive_parent_kind=admin` so the doc is excluded from future scans
//
// The CEO can skip a client. The card auto-refreshes pending count on close.
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FolderArchive, ArrowRight, CheckCircle2, SkipForward, RefreshCw, X, AlertTriangle, FileText } from "lucide-react";

export default function AdminDocsMigrationWizard() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState({ migrated: 0, moved: 0, uploaded: 0, skipped: 0 });
  const [open, setOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/drive-migration/admin-docs-pending");
      setPending(r.data || []);
      setIdx(0);
      setLastResult(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec chargement");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  // Iter-194o — accept an optional `detail.client_id` so the popup can preselect
  // a specific client when opening the wizard.
  const [preselectClientId, setPreselectClientId] = useState(null);
  useEffect(() => {
    const onOpen = (ev) => {
      setOpen(true);
      const cid = ev?.detail?.client_id || null;
      if (cid) setPreselectClientId(cid);
    };
    window.addEventListener("admin-docs-migration-open", onOpen);
    return () => window.removeEventListener("admin-docs-migration-open", onOpen);
  }, []);

  useEffect(() => {
    if (!preselectClientId || pending.length === 0) return;
    const i = pending.findIndex((c) => c.client_id === preselectClientId);
    if (i >= 0) setIdx(i);
    setPreselectClientId(null);
  }, [preselectClientId, pending]);

  const cur = pending[idx];

  const submit = async () => {
    if (!cur) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/drive-migration/${cur.client_id}/migrate-admin-docs`);
      const d = r.data || {};
      setLastResult(d);
      toast.success(
        `${cur.name} : ${d.moved_drive || 0} déplacé(s), ${d.uploaded_from_local || 0} ré-uploadé(s)` +
        (d.failures?.length ? ` · ${d.failures.length} échec(s)` : "")
      );
      setStats((s) => ({
        ...s,
        migrated: s.migrated + 1,
        moved: s.moved + (d.moved_drive || 0),
        uploaded: s.uploaded + (d.uploaded_from_local || 0),
      }));
      // Remove the migrated client from the local list and advance.
      const remaining = pending.filter((c, i) => i !== idx);
      setPending(remaining);
      setIdx(Math.min(idx, Math.max(0, remaining.length - 1)));
      window.dispatchEvent(new CustomEvent("drive-health-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration échouée");
    } finally { setBusy(false); }
  };

  const skip = () => {
    if (!cur) return;
    setStats((s) => ({ ...s, skipped: s.skipped + 1 }));
    setLastResult(null);
    if (idx < pending.length - 1) {
      setIdx(idx + 1);
    }
  };

  if (!open) {
    return (
      <div className="bg-white border border-zinc-200 rounded-lg p-6 mb-6" data-testid="admin-docs-migration-card">
        <div className="flex items-center gap-3 text-zinc-900 mb-2">
          <FolderArchive size={18} className="text-zinc-700" />
          <h2 className="font-heading font-bold text-lg">Drive · Documents administratifs</h2>
        </div>
        <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed mb-4">
          Migration des documents administratifs (contrats, NDA, statuts…) vers le
          nouveau sous-dossier dédié <code>005-Documents Administratifs</code> de
          chaque client. Le wizard crée le sous-dossier s'il manque, déplace
          les fichiers Drive existants et ré-uploade ceux stockés en fallback local.
        </p>
        <button
          onClick={() => setOpen(true)}
          data-testid="admin-docs-migration-start-btn"
          className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800"
        >
          <FolderArchive size={14} /> Démarrer la migration administrative
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-zinc-300 rounded-lg p-6 mb-6 shadow-sm" data-testid="admin-docs-migration-wizard">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 text-zinc-900">
          <FolderArchive size={18} className="text-zinc-700" />
          <h2 className="font-heading font-bold text-lg">
            Documents administratifs — étape {pending.length === 0 ? "—" : `${idx + 1}/${pending.length}`}
          </h2>
        </div>
        <button onClick={() => setOpen(false)} data-testid="admin-docs-migration-close" className="p-1.5 hover:bg-zinc-100 rounded-md">
          <X size={18} />
        </button>
      </div>

      {loading && <div className="text-sm text-zinc-500 py-8 text-center">Chargement…</div>}

      {!loading && pending.length === 0 && (
        <div className="text-center py-8 space-y-3">
          <CheckCircle2 size={36} className="mx-auto text-emerald-600" />
          <p className="text-sm text-zinc-700">Tous les documents administratifs sont rangés dans <code>005-Documents Administratifs</code>.</p>
          <p className="text-xs text-zinc-500">
            Migrés cette session : {stats.migrated} · Déplacés Drive : {stats.moved} · Ré-uploadés depuis local : {stats.uploaded} · Skip : {stats.skipped}
          </p>
          <button onClick={load} data-testid="admin-docs-migration-refresh" className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50">
            <RefreshCw size={12} /> Re-scanner
          </button>
        </div>
      )}

      {!loading && cur && (
        <div className="space-y-4">
          <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Client à traiter</div>
            <div className="font-semibold text-base text-zinc-900">{cur.name}</div>
            <div className="text-xs text-zinc-500 mt-1">
              <span className="inline-block mr-2 px-1.5 py-0.5 rounded text-[10px] font-mono bg-emerald-100 text-emerald-800">
                {cur.status}
              </span>
              {cur.country && <span className="mr-2">{cur.country}</span>}
              {cur.drive_folder_url && (
                <a href={cur.drive_folder_url} target="_blank" rel="noreferrer" className="underline">
                  Ouvrir le Drive du client ↗
                </a>
              )}
            </div>

            {/* Reason + per-doc counts */}
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <KpiPill label="Sous-dossier 005" value={cur.has_admin_subfolder ? "OK" : "Manquant"} tone={cur.has_admin_subfolder ? "ok" : "warn"} />
              <KpiPill label="Docs Drive à déplacer" value={cur.n_docs_drive_to_move} tone={cur.n_docs_drive_to_move > 0 ? "warn" : "ok"} />
              <KpiPill label="Docs locaux à ré-uploader" value={cur.n_docs_local_to_upload} tone={cur.n_docs_local_to_upload > 0 ? "warn" : "ok"} />
            </div>

            {!cur.has_admin_subfolder && (
              <div className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1.5 flex items-start gap-1.5">
                <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                <span>Le sous-dossier <code>005-Documents Administratifs</code> sera créé en Drive automatiquement à l'exécution.</span>
              </div>
            )}
          </div>

          {/* Last result trail */}
          {lastResult && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs">
              <div className="flex items-center gap-2 text-emerald-900 font-semibold mb-1">
                <CheckCircle2 size={14} /> Migration précédente — {lastResult.client_name}
              </div>
              <div className="text-emerald-800">
                Déplacés Drive : <b>{lastResult.moved_drive}</b> · Ré-uploadés depuis local : <b>{lastResult.uploaded_from_local}</b> ·
                Déjà OK : <b>{lastResult.already_in_admin}</b>
                {lastResult.failures?.length > 0 && <> · Échecs : <b>{lastResult.failures.length}</b></>}
              </div>
              {lastResult.failures?.length > 0 && (
                <ul className="mt-2 pl-4 list-disc text-red-800">
                  {lastResult.failures.slice(0, 5).map((f, i) => (
                    <li key={i}><span className="font-mono">{f.doc_id}</span>: {f.reason}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-zinc-500 italic">
            <FileText size={13} />
            <span>L'application va :</span>
            <span>① créer le sous-dossier 005 si absent</span>
            <ArrowRight size={11} />
            <span>② déplacer {cur.n_docs_drive_to_move} doc(s) Drive</span>
            <ArrowRight size={11} />
            <span>③ ré-uploader {cur.n_docs_local_to_upload} doc(s) local(aux)</span>
          </div>

          <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
            <button
              onClick={skip}
              data-testid="admin-docs-migration-skip"
              className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50"
              disabled={busy}
            >
              <SkipForward size={12} /> Passer ce client
            </button>
            <button
              onClick={submit}
              disabled={busy}
              data-testid="admin-docs-migration-apply"
              className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
            >
              {busy ? "Migration en cours…" : <>Migrer & continuer <ArrowRight size={14} /></>}
            </button>
          </div>

          <div className="text-[11px] text-zinc-400 text-right">
            Migrés cette session : {stats.migrated} · Déplacés : {stats.moved} · Ré-uploadés : {stats.uploaded} · Skip : {stats.skipped}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiPill({ label, value, tone }) {
  const cls = tone === "warn" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-emerald-50 border-emerald-200 text-emerald-900";
  return (
    <div className={`border rounded-md px-2 py-1.5 ${cls}`}>
      <div className="text-[9px] uppercase tracking-widest font-mono opacity-70">{label}</div>
      <div className="font-mono text-sm font-semibold">{value}</div>
    </div>
  );
}
